import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { MailPlus } from 'lucide-react'
import { Button, Field, FORM_INPUT_CLASS, FormError, Modal, SecretBanner } from '../../core/index.js'
import type { InvitationResult, InviteOperatorInput } from '../types.js'

const FORM_ID = 'invite-operator-form'
const LINK_BUTTON = 'text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-40'

interface InviteOperatorModalProps {
  organizationName: string | null
  /** The call that invites: the operator session's hook, or the admin's client-bound call. */
  invite: (input: InviteOperatorInput) => Promise<InvitationResult>
  onClose: () => void
}

/** The once-only banner for the link and code where the deployment cannot email (invitations plan D6). */
export function InvitationHandOffBanner({ result, onDismiss }: { result: InvitationResult; onDismiss: () => void }) {
  if (!result.handOff) {
    return null
  }
  return <SecretBanner label={`${result.invitation.email} — this deployment cannot email, so pass the link on yourself (or the code ${result.handOff.code}, entered with the email). It expires in seven days.`} value={result.handOff.link} onDismiss={onDismiss} />
}

/**
 * An operator invites a fellow operator (invitations plan D4): the login is
 * made at once, switched off, and the person is emailed a link and a code
 * to set their own password with. Opened from the operator shell's header.
 */
export function InviteOperatorModal({ organizationName, invite, onClose }: InviteOperatorModalProps) {
  const [form, setForm] = useState<InviteOperatorInput>({ firstName: '', lastName: '', email: '' })
  const [result, setResult] = useState<InvitationResult | null>(null)
  const mutation = useMutation({ mutationFn: invite, onSuccess: setResult })
  const canSubmit = form.firstName.trim() !== '' && form.lastName.trim() !== '' && form.email.trim() !== '' && !mutation.isPending

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    if (canSubmit) {
      mutation.mutate({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() })
    }
  }

  return (
    <Modal
      eyebrow="Invite an operator"
      title={result ? 'Invitation sent' : 'Invite a colleague'}
      subtitle={organizationName ?? undefined}
      canClose={!mutation.isPending}
      onClose={onClose}
      footer={
        result ? (
          <button type="button" onClick={onClose} className={LINK_BUTTON}>
            Done
          </button>
        ) : (
          <div className="flex items-center gap-4">
            <Button icon={MailPlus} type="submit" form={FORM_ID} disabled={!canSubmit}>
              {mutation.isPending ? 'Inviting…' : 'Invite'}
            </Button>
            <button type="button" onClick={onClose} disabled={mutation.isPending} className={LINK_BUTTON}>
              Cancel
            </button>
          </div>
        )
      }
    >
      {result ? (
        <div className="bg-canvas p-6 text-sm text-ink space-y-3">
          <p>
            <span className="font-bold">
              {result.invitation.firstName} {result.invitation.lastName}
            </span>{' '}
            has been invited as an operator with access to the whole of {organizationName ?? 'the client'}. They set their own password from the invitation, and can sign in once they have.
          </p>
          <InvitationHandOffBanner result={result} onDismiss={() => setResult({ ...result, handOff: null })} />
        </div>
      ) : (
        <form id={FORM_ID} onSubmit={handleSubmit} className="bg-canvas p-6 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="First name" htmlFor="invite-operator-first" required>
              <input id="invite-operator-first" className={FORM_INPUT_CLASS} value={form.firstName} onChange={(event) => setForm({ ...form, firstName: event.target.value })} required autoFocus />
            </Field>
            <Field label="Last name" htmlFor="invite-operator-last" required>
              <input id="invite-operator-last" className={FORM_INPUT_CLASS} value={form.lastName} onChange={(event) => setForm({ ...form, lastName: event.target.value })} required />
            </Field>
          </div>
          <Field label="Email" htmlFor="invite-operator-email" required hint="Their username, and where the invitation goes. One kind of login per email across the platform.">
            <input id="invite-operator-email" type="email" className={FORM_INPUT_CLASS} value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required autoComplete="off" />
          </Field>
          <p className="text-[11px] text-ink-mid">An operator has full access to every facility of the client. Nobody sees a password: they are emailed a link and a code, good for seven days, and choose their own.</p>
          <FormError error={mutation.error} fallback="The invitation could not be sent." />
        </form>
      )}
    </Modal>
  )
}
